import { notification } from 'antd';
import { useParams } from 'react-router';

import { useModalForm } from '../../../../foundation/hooks';
import { displayUnAuthorizedNotification } from '../../../../foundation/utils/notifications';
import { useGetZygosityOptionsQuery } from '../../../api/globalDataApi';
import { useUpdateSampleZygosityMutation } from '../../../api/variantApi';
import { NOTIFICATION_MESSAGES, VARIANT_STAGE_TYPES } from '../../constants';
import { VariantSample } from '../../redux';

interface FormValues {
  zygosity: string | undefined;
}

interface UseZygosityModalLogicProps {
  onClose: () => void;
  variantSamples: VariantSample[];
  selectedSample: VariantSample;
}

/**
 * Custom hook that encapsulates all the business logic for ZygosityModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useZygosityModalLogic = ({
  onClose,
  variantSamples,
  selectedSample,
}: UseZygosityModalLogicProps) => {
  const params = useParams();
  const [updateSampleZygosity] = useUpdateSampleZygosityMutation();

  const {
    isLoading: isZygosityModalLoading,
    setIsLoading: setIsZygosityModalLoading,
    user,
    stageInfo,
    getIsSaveDisabled,
    createModalOpts,
  } = useModalForm({
    onClose,
    successMessage: NOTIFICATION_MESSAGES.ZYGOSITY_UPDATED,
    errorMessage: NOTIFICATION_MESSAGES.ZYGOSITY_UPDATE_FAILED,
    stageType: VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION,
  });

  const { data: zygosityOptions = [] } = useGetZygosityOptionsQuery();

  const modalOpts = {
    ...createModalOpts('Please select a variant zygosity for the sample'),
  };

  const initialValues: FormValues = {
    zygosity: selectedSample.zygosity,
  };

  const handleFormSubmit = async (values: any) => {
    if (isZygosityModalLoading) {
      return;
    }

    if (user && params.vid) {
      try {
        setIsZygosityModalLoading(true);

        await updateSampleZygosity({
          variant_id: params.vid,
          sample_id: selectedSample.sample_id,
          requestData: {
            zygosity: values.zygosity,
          },
        }).unwrap();

        // The mutation's invalidatesTags marks the matching query tags stale, so
        // the samples list and stages refetch automatically with no manual state patching

        notification.success({
          message: NOTIFICATION_MESSAGES.SUCCESS,
          description: NOTIFICATION_MESSAGES.ZYGOSITY_UPDATED,
        });

        onClose();
      } catch (error) {
        console.error(error);
        notification.error({
          message: NOTIFICATION_MESSAGES.ERROR,
          description: NOTIFICATION_MESSAGES.ZYGOSITY_UPDATE_FAILED,
        });
      } finally {
        setIsZygosityModalLoading(false);
      }
    } else {
      displayUnAuthorizedNotification();
      return;
    }
  };

  return {
    isZygosityModalLoading,
    user,
    zygosityOptions,
    stageInfo,
    modalOpts,
    initialValues,
    getIsSaveDisabled,
    handleFormSubmit,
  };
};

export default useZygosityModalLogic;
